import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { Button, Checkbox, Heading, RadioInput, Switch } from '@mm/components';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { CampaignType } from '@mm/state/model/CampaignsModel';

import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  CampaignTypeWithMappedPurposeAndDisplayStatus,
  selectCampaignById,
} from '@mm/state/selectors/campaignSelectors';
import { selectEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';

import 'prosemirror-view/style/prosemirror.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { PrimaryToolTip } from '../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import css from './AutomationEmailSmsSettingsSlideout.module.css';
import { ReactComponent as SendGridIcon } from 'assets/sendgrid.svg';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';

export type IAutomationEmailSmsSettingsSlideout = {
  className?: string;
  campaignId?: number;
};

const mapAccountToOpt = ({ id, emailAddress, kind }) => ({
  id,
  name: emailAddress,
  isSendGrid: kind === 'sendgrid',
});

export const AutomationEmailSmsSettingsSlideout: React.FC<
  IAutomationEmailSmsSettingsSlideout
> = props => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { campaignId } = props;

  const campaign = useSelector<
    StoreState,
    CampaignTypeWithMappedPurposeAndDisplayStatus | undefined
  >(
    state => selectCampaignById(state, campaignId || -1),
    (l, r) =>
      JSON.stringify({
        a: l?.emailAccountsIds,
        b: l?.suppressionListEnabled,
      }) ===
      JSON.stringify({ a: r?.emailAccountsIds, b: r?.suppressionListEnabled }),
  );

  const hideSMSSettings =
    campaign?.details?.automationTemplate === 'email_sold_automation';
  const defaultEmailAccountsIds = campaign?.emailAccountsIds || [];
  const defaultSuppressionListEnabled = !!campaign?.suppressionListEnabled;

  const isDefaultRobin = defaultEmailAccountsIds.length > 1;

  const [opt, setOpt] = useState(isDefaultRobin ? 'round_robin' : 'single');

  const accounts = useSelector(selectEmailAccounts);

  const defaultOptions = accounts.map(mapAccountToOpt);

  const defaultEmailSelected =
    defaultEmailAccountsIds.length === 1
      ? defaultEmailAccountsIds[0]
      : undefined;

  const defaultCheckboxes = isDefaultRobin
    ? defaultEmailAccountsIds.reduce((a, v) => {
        return {
          ...a,
          [v]: true,
        };
      }, {})
    : {};

  const [emailOptions, setEmailOptions] = useState(defaultOptions);
  const [checkboxes, setCheckboxes] =
    useState<Record<number, boolean>>(defaultCheckboxes);

  const onCheckboxClick = id => {
    setCheckboxes({ ...checkboxes, [id]: !checkboxes[id] });
  };

  const [emailSelectedId, setEmailSelectedId] = useState<number | undefined>(
    defaultEmailSelected,
  );

  const onEmailOptionsChange = opts => {
    setEmailSelectedId(opts.id);
  };

  const [isEmailSuppressionListEnabled, setIsEmailSuppressionListEnabled] =
    useState(defaultSuppressionListEnabled);

  const history = useHistory();

  const modalActions = useModalActions();

  const [busy, setBusy] = useState(false);

  const campaignCRUDActions = useCampaignCRUDActions();

  const isSaveDisabled = busy;

  const getPayload = () => {
    const payload = {
      id: campaignId,
      emailAccountsIds: [],
      suppressionListEnabled: isEmailSuppressionListEnabled,
    } as unknown as CampaignType;

    switch (opt) {
      case 'single': {
        payload.emailAccountsIds = emailSelectedId ? [emailSelectedId] : [];
        break;
      }
      case 'round_robin': {
        payload.emailAccountsIds = Object.keys(checkboxes)
          .filter(k => checkboxes[k])
          .map(v => parseInt(v, 10));
        break;
      }
      default:
        break;
    }

    return payload;
  };

  const payload = useMemo(() => {
    return getPayload();
  }, [opt, checkboxes, emailSelectedId]);

  const hasNoEmailChanges =
    payload.emailAccountsIds?.sort().join('') ===
    defaultEmailAccountsIds.sort().join('');

  const hasNoContactListChanges =
    !!payload.suppressionListEnabled === defaultSuppressionListEnabled;

  const hasNoChanges = hasNoEmailChanges && hasNoContactListChanges;

  const onSave = async () => {
    setBusy(true);
    try {
      const payload = getPayload();

      const res = await campaignCRUDActions.updateCampaign(payload, chatbotId);

      const relatedCampaignId = res?.campaign?.relatedCampaigns?.[0]?.id;

      if (relatedCampaignId) {
        await campaignCRUDActions.retrieve(relatedCampaignId);
      }

      successToast('Your changes have been saved.', 'top-left');

      closeSlideout();
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const onBeforeClose = () => {
    if (hasNoChanges) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      modalActions.openGeneralConfirmModal({
        headerText: 'You have unsaved changes',
        messageText: 'Are you sure that you want to leave without saving?',
        onCloseHandler: ok => {
          if (ok) {
            resolve(true);
          } else {
            reject();
          }
        },
        okButtonText: "Yes I'm sure",
        cancelButtonText: 'Cancel',
      });
    });
  };

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
    } catch (err) {}
  };

  const { setRef, close: closeSlideout } = useSlideoutOnClose(onBeforeClose);

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const emailAccountsCRUDActions = useEmailAccountCRUDActions();

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const res = await emailAccountsCRUDActions.getAccounts(activeFbPageId);

        if (Array.isArray(res?.emailAccounts)) {
          const mapped = res.emailAccounts.map(mapAccountToOpt);
          setEmailOptions(mapped);

          if (!defaultEmailAccountsIds.length) {
            setEmailSelectedId(res.emailAccounts[0]?.id);
          }
        }
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  const configureEmailSendersContent = (
    <>
      <RadioInput
        disabled={isSaveDisabled}
        className={css.radio}
        checked={opt === 'single'}
        name="Use a single email"
        label="Use a single email"
        value="single"
        onClick={() => setOpt('single')}
      />

      {opt === 'single' && (
        <PrimarySelect
          disabled={isSaveDisabled}
          className={css.select}
          options={emailOptions}
          value={emailOptions.find(
            v =>
              emailSelectedId && v.id.toString() === emailSelectedId.toString(),
          )}
          onChange={onEmailOptionsChange}
        />
      )}

      <div className={css.robin}>
        <RadioInput
          disabled={isSaveDisabled}
          className={css.radio}
          checked={opt === 'round_robin'}
          name="Multiple Emails (Round Robin)"
          label="Multiple Emails (Round Robin)"
          value="round_robin"
          onClick={() => setOpt('round_robin')}
        />
        <PrimaryToolTip
          useHover
          classNameContent={css.infoTooltipContent}
          anchorElement={
            <span className={css.info}>
              <QuestionCircleOutlined />
            </span>
          }
        >
          <span className={css.popover}>
            Emails will be rotated through to start new Conversations with
            Leads. Leads with existing Email Conversations will continue to
            receive emails from the same Email Address.
          </span>
        </PrimaryToolTip>
      </div>

      {opt === 'round_robin' && (
        <>
          <div className={css.checkboxes}>
            {emailOptions.map(v => (
              <Checkbox
                disabled={isSaveDisabled}
                isChecked={checkboxes[v.id]}
                onClick={() => onCheckboxClick(v.id)}
                label={
                  <div className={css.checkboxLabel}>
                    {v.name}
                    {v.isSendGrid && <SendGridIcon style={{ width: '14px' }} />}
                  </div>
                }
                key={v.id}
              />
            ))}
          </div>
          <Button
            css={{ container: css.connect }}
            color="secondary"
            onClick={() => {
              closeSlideout();
              modalActions.openActivateEmailAccountModal();
            }}
          >
            Connect new email
          </Button>
        </>
      )}
    </>
  );
  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <Heading size={4}>
          {hideSMSSettings ? 'Email Settings' : 'Email & SMS Settings'}
        </Heading>
        <Button
          css={{ container: css.save }}
          onClick={onSave}
          disabled={isSaveDisabled}
        >
          Save
        </Button>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        {hideSMSSettings ? (
          <section className={css.emailOnlySettings}>
            <div className={css.emailOnlySettingsHeading}>
              Choose which accounts you will contact your leads with
            </div>
            {configureEmailSendersContent}
          </section>
        ) : (
          <>
            <section>
              <CollapsibleContentContainer
                orderNumber="1"
                containerName="Configure Email Senders"
                stepperStylingPlatform="facebook"
                useCircleNumberStyle
                expandedOnMount
                hideTitleBorder
                className={css.dropdown}
              >
                {configureEmailSendersContent}
              </CollapsibleContentContainer>
            </section>
            <section className={css.disabled}>
              <CollapsibleContentContainer
                orderNumber="2"
                containerName="Configure SMS Senders"
                stepperStylingPlatform="facebook"
                expandedOnMount={false}
                useCircleNumberStyle
                hideTitleBorder
              />
            </section>
          </>
        )}
        <section className={css.contactList}>
          <Switch
            checked={isEmailSuppressionListEnabled}
            disabled={busy}
            onChange={() =>
              setIsEmailSuppressionListEnabled(!isEmailSuppressionListEnabled)
            }
            css={{ container: css.contactListSwitch }}
          />

          <Heading size={4}>Use Email Suppression List</Heading>

          <Button
            css={{ container: css.contactListButton }}
            color="gray"
            onClick={() => {
              history.push(
                BotBuilderRoutes.integrations.getLink({ chatbotId }),
                {
                  kind: 'contact-suppression',
                },
              );
              closeSlideout();
              // modalActions.openEmailSuppressionListModal()
            }}
          >
            Settings
          </Button>
        </section>
      </Menu.Content>
    </Menu>
  );
};
